import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  User,
  Calendar,
  Send,
  Clock,
  MessageCircle,
  Tag,
  AlertCircle
} from 'lucide-react';
import supportService from '@services/support.service';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import LoadingSpinner from '@components/common/LoadingSpinner';
import toast from 'react-hot-toast';

const SupportTicketDetails = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchTicketDetails();
  }, [ticketId]);

  const fetchTicketDetails = async () => {
    setLoading(true);
    try {
      const response = await supportService.getTicketById(ticketId);
      if (response.success) {
        setTicket(response?.data?.data);
      }
    } catch (error) {
      toast.error('Failed to load ticket details');
      navigate('/support');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);
    try {
      const response = await supportService.addResponse(ticketId, message);
      if (response.success) {
        toast.success('Response sent');
        setMessage('');
        fetchTicketDetails();
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      'open': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Open' },
      'in_progress': { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'In Progress' },
      'waiting_for_user': { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Waiting for Your Response' },
      'resolved': { bg: 'bg-green-100', text: 'text-green-700', label: 'Resolved' },
      'closed': { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Closed' },
      'reopened': { bg: 'bg-red-100', text: 'text-red-700', label: 'Reopened' },
    };
    const badge = badges[status] || badges['open'];
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  if (loading || !ticket) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gradient-to-br from-gray-50 to-gray-100 p-3">
      <div className="w-full">
        <div className="mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/support')}
            className="mb-3 hover:bg-gray-100 transition-all duration-200 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tickets
          </Button>
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              Ticket #{ticket.ticketNumber}
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <Card className="p-4 shadow-lg hover:shadow-xl transition-shadow duration-300 border-0">
              <div className="mb-4">
                <h2 className="text-xl font-bold text-gray-900 mb-3">
                  {ticket.subject}
                </h2>
                <div className="flex flex-wrap items-center gap-2">
                  {getStatusBadge(ticket.status)}
                  <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm ${
                    ticket.priority === 'urgent' ? 'bg-red-100 text-red-700 border border-red-200' :
                    ticket.priority === 'high' ? 'bg-orange-100 text-orange-700 border border-orange-200' :
                    ticket.priority === 'medium' ? 'bg-blue-100 text-blue-700 border border-blue-200' :
                    'bg-gray-100 text-gray-700 border border-gray-200'
                  }`}>
                    <AlertCircle className="w-3 h-3" />
                    {ticket.priority} Priority
                  </span>
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-200 shadow-sm capitalize">
                    <Tag className="w-3 h-3" />
                    {ticket.category?.replace('_', ' ')}
                  </span>
                </div>
              </div>

              <div className="mb-4 p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-100 shadow-sm">
                <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">
                  {ticket.description}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600 pt-3 border-t border-gray-100">
                <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                  <User className="w-4 h-4 text-blue-600" />
                  <span className="font-medium">{ticket.user?.email}</span>
                </div>
                <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-lg">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  <span>{new Date(ticket.created_at).toLocaleString()}</span>
                </div>
              </div>
            </Card>

            <Card className="p-4 shadow-lg hover:shadow-xl transition-shadow duration-300 border-0">
              <div className="flex items-center gap-2 mb-4">
                <MessageCircle className="w-5 h-5 text-blue-600" />
                <h3 className="text-lg font-bold text-gray-900">
                  Conversation History
                </h3>
              </div>

              <div className="space-y-3 mb-4 max-h-[500px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                {ticket.responses && ticket.responses.length > 0 ? (
                  ticket.responses.map((response) => (
                    <div
                      key={response.id}
                      className={`p-3 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md ${
                        response.user?.role === 'admin'
                          ? 'bg-gradient-to-br from-blue-50 to-blue-100 border-l-4 border-blue-500'
                          : 'bg-white border-l-4 border-gray-300'
                      }`}
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md ${
                          response.user?.role === 'admin' 
                            ? 'bg-gradient-to-br from-blue-500 to-blue-600' 
                            : 'bg-gradient-to-br from-purple-500 to-pink-500'
                        }`}>
                          {response.user?.email?.[0]?.toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <p className="text-sm font-bold text-gray-900">
                              {response.user?.role === 'admin' ? '🎧 Support Team' : response.user?.email}
                            </p>
                            <p className="text-xs text-gray-500 bg-white px-2 py-1 rounded-md shadow-sm">
                              {response.created_at ? new Date(response.created_at).toLocaleString() : 'Invalid Date'}
                            </p>
                          </div>
                          <p className="text-gray-800 whitespace-pre-wrap leading-relaxed mt-2">
                            {response.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <div className="inline-block p-3 bg-gray-100 rounded-full mb-2">
                      <MessageCircle className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-gray-500 font-medium">
                      No responses yet
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                      Start the conversation by sending a message below
                    </p>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 pt-4 mt-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Message
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message here..."
                  rows={3}
                  className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none mb-3 transition-all duration-200 shadow-sm hover:border-gray-300"
                />

                <Button
                  variant="primary"
                  onClick={handleSendMessage}
                  disabled={sending || !message.trim()}
                  loading={sending}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Response
                </Button>
              </div>
            </Card>
          </div>

          <div className="space-y-4">
            <Card className="p-4 shadow-lg hover:shadow-xl transition-shadow duration-300 border-0 bg-gradient-to-br from-white to-gray-50">
              <h3 className="text-base font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                Ticket Information
              </h3>
              <div className="space-y-2 text-sm">
                <div className="p-2 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-xs text-gray-600 mb-1 font-medium">Ticket Number</p>
                  <p className="font-bold text-gray-900 font-mono">{ticket.ticketNumber}</p>
                </div>
                <div className="p-2 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-2 font-medium">Status</p>
                  <div>
                    {getStatusBadge(ticket.status)}
                  </div>
                </div>
                <div className="p-2 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1 font-medium">Priority</p>
                  <p className="font-semibold text-gray-900 capitalize">{ticket.priority}</p>
                </div>
                <div className="p-2 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1 font-medium">Category</p>
                  <p className="font-semibold text-gray-900 capitalize">{ticket.category?.replace('_', ' ')}</p>
                </div>
                <div className="p-2 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1 font-medium">Created</p>
                  <p className="font-semibold text-gray-900 text-xs">
                    {new Date(ticket.created_at).toLocaleString()}
                  </p>
                </div>
                {ticket.resolvedAt && (
                  <div className="p-2 bg-green-50 rounded-lg border border-green-100">
                    <p className="text-xs text-gray-600 mb-1 font-medium">Resolved</p>
                    <p className="font-semibold text-gray-900 text-xs">
                      {new Date(ticket.resolved_at).toLocaleString()}
                    </p>
                  </div>
                )}
                <div className="p-2 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600 mb-1 font-medium">Last Updated</p>
                  <p className="font-semibold text-gray-900 text-xs">
                    {new Date(ticket.updatedAt).toLocaleString()}
                  </p>
                </div>
                {ticket.assignedAdmin && (
                  <div className="p-2 bg-purple-50 rounded-lg border border-purple-100">
                    <p className="text-xs text-gray-600 mb-1 font-medium">Assigned To</p>
                    <p className="font-semibold text-gray-900 text-xs">
                      {ticket.assignedAdmin.email}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportTicketDetails;
